import { useQueryClient } from "@tanstack/react-query";
import polly from "polly-js";
import rg4js from "raygun4js";
import { useCallback, useEffect, useState } from "react";
import {
  ContactInfoType,
  DocumentSubType,
  DocumentType,
  type FormInt,
  type InterrogatoryResponseItem,
  type Objection,
  ObjectionType,
} from "@/briefpoint-client";
import type { FormIntExtended } from "@/components/DocumentWizard/DocumentWizard";
import { getRespondentDisplay } from "@/components/DocumentWizard/SpecialInterrogatoriesStep/Selections/utils";
import {
  getDocumentOptions,
  getUnarchivedCaseDocumentListOptions,
  getUnarchivedDocumentListOptions,
} from "@/lib/query-options";
import { queryClient } from "@/lib/queryClient";
import type {
  DocumentDetailClient,
  FirmResponse,
  InterrogatoryResponseMultiple,
  UserResponse,
} from "@/lib/types";
import { addUploadDate, getUrl, normalizeError } from "@/lib/utils";
import { useDocumentApi, useObjectionApi } from "./useApi";
import useMixpanel from "./useMixpanel";

export type SetResponseForInterrogatoryFunction = (
  interrogatoryId: number,
  newSelections: InterrogatoryResponseMultiple,
  objections: Objection[],
  formInt: FormIntExtended | null,
  user: UserResponse | undefined,
  firm: FirmResponse | undefined,
  justAddedObjectionId: number | undefined,
) => SaveInterrogatoryParams | undefined;

export type SaveInterrogatoryParams = {
  documentId: string;
  interrogatoryId: number;
  updateInterrogatory: {
    responseList: {
      [key: string]: InterrogatoryResponseItem[];
    };
    complete: boolean;
  };
};

type UseDocumentsType = [
  DocumentDetailClient | undefined,
  SetResponseForInterrogatoryFunction,
  (document: DocumentDetailClient) => Promise<void>,
  (checkLocalHost?: boolean) => Promise<string>,
  (
    number: number,
    text?: string,
    interrogatoryId?: number,
    pleadDocId?: string,
    sourceId?: string,
  ) => Promise<void>,
  (interrogatoryId: number, pleadDocId?: string) => Promise<void>,
  (document: DocumentDetailClient) => Promise<void>,
  (toSave: SaveInterrogatoryParams) => Promise<void>,
  () => Promise<void>,
  (index: number) => Promise<void>,
];
export default function useDocumentDetail(
  documentId: string,
): UseDocumentsType {
  const [document, setDocument] = useState<DocumentDetailClient>();
  const documentApi = useDocumentApi();
  const [mixpanelTrack] = useMixpanel();
  const queryClient = useQueryClient();

  const loadData = useCallback(async () => {
    try {
      const document = await documentApi.documentGet({ documentId });
      setDocument(addUploadDate(document) as DocumentDetailClient);
    } catch (error) {
      rg4js("send", {
        error: normalizeError(error),
      });
      console.error("Failed to load document:", error);
      // Re-throw to allow error boundary to catch this
      throw new Error("Failed to load document");
    }
  }, [documentId, documentApi]);

  /* biome-ignore lint/correctness/useExhaustiveDependencies: ignore loadData */
  useEffect(() => {
    if (documentId) {
      loadData();
    }
  }, [documentId]);

  const addEditInterrogatory = useCallback(
    async (
      number: number,
      text?: string,
      interrogatoryId?: number,
      pleadDocId?: string,
      sourceId?: string,
    ) => {
      if (interrogatoryId) {
        if (text) {
          await documentApi.documentUpdateInterrogatory({
            documentId: pleadDocId || documentId,
            interrogatoryId: interrogatoryId,
            updateInterrogatory: { text: text, number: number, sourceId },
          });
        } else {
          await documentApi.documentUpdateInterrogatory({
            documentId: pleadDocId || documentId,
            interrogatoryId: interrogatoryId,
            updateInterrogatory: { number: number, sourceId },
          });
        }
      } else {
        await documentApi.documentAddInterrogatory({
          documentId: pleadDocId || documentId,
          updateInterrogatory: { text: text, number: number, sourceId },
        });
      }
      // TODO: could this be done w/o re-getting the full document from the server?
      // especially for editing could we just update the interrogatory in place?s
      const updatedDocument = await documentApi.documentGet({
        documentId: pleadDocId || documentId,
      });
      setDocument(addUploadDate(updatedDocument) as DocumentDetailClient);
      // Invalidate React Query cache so other components using getDocumentOptions get fresh data
      queryClient.invalidateQueries({
        queryKey: getDocumentOptions(pleadDocId || documentId, documentApi)
          .queryKey,
      });
    },
    [documentApi, documentId, queryClient],
  );

  const deleteInterrogatory = useCallback(
    async (interrogatoryId: number, pleadDocId?: string) => {
      await documentApi.documentDeleteInterrogatory({
        documentId: pleadDocId || documentId,
        interrogatoryId: interrogatoryId,
      });
      const updatedDocument = await documentApi.documentGet({
        documentId: pleadDocId || documentId,
      });
      setDocument(addUploadDate(updatedDocument) as DocumentDetailClient);
      queryClient.invalidateQueries({
        queryKey: getDocumentOptions(pleadDocId || documentId, documentApi)
          .queryKey,
      });
    },
    [documentApi, documentId, queryClient],
  );

  const clearMissingFormInterrogatory = useCallback(async () => {
    await documentApi.documentClearMissingFormInterrogatory({
      documentId: documentId,
    });
    const updatedDocument = await documentApi.documentGet({ documentId });
    setDocument(addUploadDate(updatedDocument) as DocumentDetailClient);
    queryClient.invalidateQueries({
      queryKey: getDocumentOptions(documentId, documentApi).queryKey,
    });
  }, [documentApi, documentId, queryClient]);

  const updateDocumentCallback = useCallback(
    async (doc: DocumentDetailClient) => {
      await documentApi.documentUpdateDocument({
        documentId: doc.id,
        document: doc,
      });
      setDocument({ ...doc });
      queryClient.invalidateQueries({
        ...getUnarchivedCaseDocumentListOptions(documentApi),
      });
      queryClient.invalidateQueries({
        ...getUnarchivedDocumentListOptions(documentApi),
      });
    },
    [documentApi, queryClient],
  );

  const clearMismatchWarning = useCallback(
    async (doc: DocumentDetailClient) => {
      if (!doc.info) return;

      doc.info.firstMismatchedRequest = null;
      await documentApi.documentDismissMismatchWarning({ documentId: doc.id });
      setDocument({ ...doc });
    },
    [documentApi],
  );

  // I updated this to return a function to the actual API call so we can make a queue of actual saves, while still updating the UI as users click
  // there's probably a better way to do this, if you know what that is, be my guest!
  const setResponse: SetResponseForInterrogatoryFunction = (
    interrogatoryId,
    newSelections,
    objections,
    formInt,
    user,
    firm,
    justAddedObjectionId,
  ) => {
    const interrogatory = document?.interrogatories?.find(
      (ig) => ig.id === interrogatoryId,
    );
    if (!interrogatory) return;

    let complete = false;
    if (document?.info?.documentType !== DocumentType.Pleading) {
      let finishObjections = objections.filter(
        (obj) =>
          obj.objectionType === ObjectionType.Response ||
          obj.objectionType === ObjectionType.NoResponse,
      );
      interrogatory.responseList = Object.assign({}, newSelections);
      if (
        finishObjections?.some((obj) => interrogatory.responseList?.[obj.id])
      ) {
        // finish is selected
        complete = true;
      }

      // This whole mess decides if we want to mark other requests completed based on the answer to this one
      // currently this only happens for 'conditionalSkipsEntireSection' FormInts
      finishObjections = objections.filter(
        (obj) => obj.objectionType === ObjectionType.Response,
      );
      const finish = finishObjections.find(
        (obj) => interrogatory.responseList?.[obj.id],
      );

      if (
        finish &&
        formInt &&
        interrogatory.responseList?.[finish.id]?.find(
          (x) => x.objectionId === justAddedObjectionId,
        )
      ) {
        if (interrogatory.responseList?.[finish.id]?.[0]) {
          interrogatory.responseList[finish.id][0].additionalText = undefined; // Response type objections are only allowed 1 response
        }
      }

      // oh, also autofill their attorney info for 1.1, 70.1, 101.1, 301.1
      if (
        user &&
        firm &&
        finish &&
        formInt &&
        (formInt.section === 1 ||
          formInt.section === 70 ||
          formInt.section === 101 ||
          formInt.section === 301) &&
        formInt.number === 1
      ) {
        const response = interrogatory.responseList?.[finish.id]?.[0];
        if (!response?.additionalText) {
          let customResponse = `${user.firstName} ${user.lastName}, ${firm.name}`;
          const phone = firm.contactInfo.find(
            (c) => c.type === ContactInfoType.Phone,
          );

          if (firm.address?.street) {
            customResponse += `, ${firm.address.street}`;
            if (firm.address.street2) {
              customResponse += `, ${firm.address.street2}`;
            }

            customResponse += `, ${firm.address.city}, ${firm.address.stateProvince} ${firm.address.postalCode}`;
          }
          customResponse += ",";
          if (phone) {
            customResponse += ` ${phone.value},`;
          }
          customResponse += document?.info
            ? ` Attorney for ${getRespondentDisplay(document.info)}.`
            : "";
          response.additionalText = customResponse;
        }
      }

      // if this interrogatory is already complete (which means it previously had a Response) or we are now Finishing,
      // AND this is an item that might want to manage completion of other items
      if (
        (interrogatory.complete || finish) &&
        formInt &&
        formInt.conditionalSkipsEntireSection &&
        formInt.conditionalSkipsIds
      ) {
        const response = finish && interrogatory.responseList?.[finish.id];
        // if there are any interrogatories in this form int's 'conditionalSkipsIds' that don't have any response items (don't override people being weird)
        const intsToSkip = document?.interrogatories?.filter(
          (ig) =>
            formInt.conditionalSkipsIds?.includes(ig.text) &&
            !Object.entries(ig.response).length,
        );
        // for each one, mark it complete if they answered "no" to this question
        intsToSkip?.forEach(async (ig, _) => {
          const currentComplete = ig.complete;
          const newComplete =
            complete && !!response && response[0].additionalText !== "yes";
          ig.complete = newComplete;
          formInt.shouldSkipRemainderOfSection = newComplete;
          // and save the other question if we're changing the value
          if (currentComplete !== newComplete) {
            polly()
              .waitAndRetry(2)
              .executeForPromise(async () => {
                await documentApi.documentUpdateInterrogatory({
                  documentId,
                  interrogatoryId: ig.id,
                  updateInterrogatory: {
                    response: ig.response,
                    complete: newComplete,
                  },
                });
              })
              .catch((c) => console.error(c));
          }
        });
      }

      interrogatory.complete = complete;
    }
    if (!document) return;
    setDocument({
      ...document,
      interrogatories: [...document.interrogatories],
    });

    interrogatory.responseList = interrogatory.responseList || {};

    return {
      documentId,
      interrogatoryId,
      updateInterrogatory: {
        responseList: interrogatory.responseList,
        complete,
      },
    };
  };

  const updateInterrogatory = useCallback(
    async (toSave: SaveInterrogatoryParams) => {
      await polly()
        .waitAndRetry(2)
        .executeForPromise(async () => {
          await documentApi.documentUpdateInterrogatory({
            documentId: toSave.documentId,
            interrogatoryId: toSave.interrogatoryId,
            updateInterrogatory: {
              responseList: toSave.updateInterrogatory.responseList,
              complete: toSave.updateInterrogatory.complete,
            },
          });
        })
        .catch((e) => {
          return (e?.text?.() || Promise.resolve("unknown")).then(
            (error: unknown) => {
              rg4js("send", {
                error: normalizeError(error),
              });
              throw e;
            },
          );
        });
    },
    [documentApi],
  );

  const getReviewPreviewUrl = useCallback(
    async (checkLocalhost = false, viewType = "Download") => {
      const API_URL = import.meta.env.VITE_API_URL;
      if (checkLocalhost && API_URL.includes("localhost")) {
        // the word document previewer can't use localhost urls
        const url = await documentApi.documentGenerateDocument({
          documentId,
          upload: true,
        });
        return url;
      }
      const token = await documentApi.documentGenerateAccessToken({
        documentId,
      });
      const url = getUrl("document/generate-doc-accesstoken", {
        documentId,
        tokenId: token.id || "",
        t: String(Date.now()),
      });
      mixpanelTrack("DocumentView", {
        Method: viewType,
        DocumentName: document?.name,
        DocumentId: documentId,
      });
      return url;
    },
    [documentApi, documentId, document, mixpanelTrack],
  );

  const checkForInterrogatoryUpdate = useCallback(
    async (index: number) => {
      const interrogatory = document?.interrogatories?.[index];

      if (
        !interrogatory ||
        !interrogatory.responseList ||
        Object.keys(interrogatory.responseList).length
      ) {
        return;
      }

      const update = await documentApi.documentGetInterrogatory({
        documentId: document?.id,
        interrogatoryId: interrogatory.id,
      });

      if (
        update?.responseList &&
        Object.keys(update.responseList).length &&
        document?.interrogatories
      ) {
        document.interrogatories[index] = update;
        setDocument({
          ...document,
          interrogatories: [...document.interrogatories],
        });
      }
    },
    [document, documentApi],
  );

  return [
    document,
    setResponse,
    updateDocumentCallback,
    getReviewPreviewUrl,
    addEditInterrogatory,
    deleteInterrogatory,
    clearMismatchWarning,
    updateInterrogatory,
    clearMissingFormInterrogatory,
    checkForInterrogatoryUpdate,
  ];
}

export function useDocumentFormInterrogatories(
  subType?: DocumentSubType,
): FormInt[] | null {
  const [formInterrogatories, setFormInterrogatories] = useState<
    FormInt[] | null
  >(null);
  const objectionApi = useObjectionApi();

  useEffect(() => {
    if (
      !formInterrogatories &&
      subType === DocumentSubType.FormInterrogatorries
    )
      objectionApi.objectionGetFormInts().then((formInts) => {
        setFormInterrogatories(formInts);
      });
  }, [objectionApi, subType, formInterrogatories]);

  return formInterrogatories;
}
